import React, { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { getLayoutForRole } from '@/core/auth/RouteGuards';

/**
 * AdaptiveLayout — Renders the appropriate layout shell based on the user's role.
 * Used for shared /workspace/* routes that any role can access if they have permission.
 *
 * This avoids duplicating routes under each portal section and ensures students,
 * agents, and consultancy admins all see the correct layout when accessing
 * shared catalog pages like /workspace/countries.
 */

const MainLayout       = lazy(() => import('./MainLayout').then(m => ({ default: m.MainLayout })));
const SuperAdminLayout = lazy(() => import('@/features/superadmin').then(m => ({ default: m.SuperAdminLayout })));
const AgentLayout      = lazy(() => import('@/features/agent').then(m => ({ default: m.AgentLayout })));
const StudentLayout    = lazy(() => import('@/features/student').then(m => ({ default: m.StudentLayout })));

const LayoutLoading = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#F8F7FA',
        color: '#9092A9',
        fontSize: '0.875rem',
    }}>
        Loading workspace…
    </div>
);

export const AdaptiveLayout: React.FC = () => {
    const { user } = useAuth();
    const layoutType = getLayoutForRole(user);

    return (
        <Suspense fallback={<LayoutLoading />}>
            {layoutType === 'superadmin' && <SuperAdminLayout />}
            {layoutType === 'agent' && <AgentLayout />}
            {layoutType === 'student' && <StudentLayout />}
            {layoutType === 'consultancy' && <MainLayout />}
        </Suspense>
    );
};
